/**
 * Things that happen once in the life of an account and are worth remembering
 * afterwards: a ceremony that has been played, a saga that has been followed
 * to its end, an easter egg that has been found.
 *
 * ── Why these are awards ────────────────────────────────────────────────────
 * They have to be once-per-*account*, not once-per-install. Both residents
 * carry a phone and an installed PWA, and Dexie meta is per-device — a
 * hundred-day ceremony that plays again on the other phone is not a ceremony,
 * it is a bug with music. The awards table already syncs, already clears on
 * `adoptUser`, and already stores exactly this shape.
 *
 * ── Why `beat` and not a new kind ───────────────────────────────────────────
 * The obvious move is a third `AwardKind`. That means a Zod change, a Postgres
 * enum change and a `CHECK` constraint migration, deployed to the API strictly
 * before the client that writes the new value — or sync rejects every row. For
 * a feature that gains nothing from the distinction, that is a live-database
 * migration bought for a label.
 *
 * `beat` already means, in the schema's own words, "one-shot story beat",
 * which is precisely what these are. The trophy cabinet is unaffected because
 * it renders by mapping over `TROPHIES` and looking each key up — a row whose
 * key is not a trophy simply has nowhere to appear. The `moment.` prefix keeps
 * the two sets apart for anything that needs to tell them apart.
 */

import { grantAward, hasAward, listAwards } from '../db/repo.js'

/** Everything in this file is keyed under one prefix; see the header. */
const PREFIX = 'moment.'

export type MomentKind = 'ceremony' | 'saga' | 'secret'

export function momentKey(kind: MomentKind, id: string): string {
  return `${PREFIX}${kind}.${id}`
}

/** Has this one already happened for this account? */
export function momentSeen(kind: MomentKind, id: string): Promise<boolean> {
  return hasAward(momentKey(kind, id))
}

/**
 * Mark it as having happened. Returns true if this call is the one that did
 * it — `grantAward` already treats a second grant of the same key as a no-op,
 * so a ceremony interrupted and reopened cannot double-record itself.
 */
export async function markMoment(kind: MomentKind, id: string): Promise<boolean> {
  const row = await grantAward({ kind: 'beat', awardKey: momentKey(kind, id) })
  return row != null
}

export interface MomentRecord {
  kind: MomentKind
  id: string
  /** Local date it was recorded, `YYYY-MM-DD`. */
  on: string
}

/** Everything recorded so far, newest first. The dossier reads this. */
export async function listMoments(): Promise<MomentRecord[]> {
  const rows = await listAwards()
  return rows.flatMap((row) => {
    if (!row.awardKey.startsWith(PREFIX)) return []
    const rest = row.awardKey.slice(PREFIX.length)
    const cut = rest.indexOf('.')
    if (cut === -1) return []
    const kind = rest.slice(0, cut)
    if (kind !== 'ceremony' && kind !== 'saga' && kind !== 'secret') return []
    return [{ kind, id: rest.slice(cut + 1), on: row.awardedOn }]
  })
}
